import { useState } from "react";
import useSWR from "swr";
import dayjs from "dayjs";
import {
  Badge,
  Button,
  Checkbox,
  Group,
  Menu,
  Modal,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCheck, IconChevronDown, IconClock, IconX } from "@tabler/icons-react";
import { fetcher } from "../../../../services/fetcher";
import { errorNotifications, successNotifications } from "../../../../utils/handleNotifications";
import { FOLLOWUP_PRIORITY_OPTIONS, FOLLOWUP_TYPE_OPTIONS } from "../../utils/leadsOptions";

const OUTCOMES = [
  { value: "INTERESTED", label: "Interested" },
  { value: "NOT_INTERESTED", label: "Not interested" },
  { value: "NO_ANSWER", label: "No answer" },
  { value: "CALLBACK", label: "Callback" },
];

const TITLE = {
  done: "Complete follow-up",
  reschedule: "Reschedule follow-up",
  cancel: "Cancel follow-up",
};

const SUCCESS = {
  done: "Follow-up completed",
  reschedule: "Follow-up rescheduled",
  cancel: "Follow-up cancelled",
};

const SAVE = { done: "Complete", reschedule: "Reschedule", cancel: "Cancel" };

const historyLine = (event) =>
  [event.title, event.actor?.name, event.created_on ? dayjs(event.created_on).format("DD MMM YYYY, hh:mm a") : null]
    .filter(Boolean)
    .join(" · ");

const TIME_OPTIONS = Array.from({ length: 96 }, (_, index) => {
  const value = dayjs().startOf("day").add(index * 15, "minute").format("HH:mm");
  return { value, label: value };
});

const withTime = (date, time) => {
  const [hours, minutes] = (time || "00:00").split(":").map(Number);
  return dayjs(date || new Date()).hour(hours).minute(minutes).second(0).millisecond(0).toDate();
};

const RecentHistory = ({ items }) => (
  <Stack gap={4}>
    <Text size="xs" fw={700} c="dimmed" tt="uppercase">Recent history</Text>
    {items.length ? items.map((event) => (
      <Text key={event.id} size="sm" c="dimmed">{historyLine(event)}</Text>
    )) : (
      <Text size="sm" c="dimmed">No recent history</Text>
    )}
  </Stack>
);

const DueDateTime = ({ label, value, onChange }) => {
  const current = value ? dayjs(value) : null;
  const time = current ? current.format("HH:mm") : null;
  const times = time && TIME_OPTIONS.every((item) => item.value !== time)
    ? [{ value: time, label: time }, ...TIME_OPTIONS]
    : TIME_OPTIONS;

  return (
    <Input.Wrapper label={label} withAsterisk>
      <SimpleGrid cols={2} spacing="xs" mt={4}>
        <DatePickerInput
          placeholder="dd-mm-yyyy"
          value={current?.toDate() || null}
          minDate={dayjs().startOf("day").toDate()}
          valueFormat="DD-MM-YYYY"
          popoverProps={{ withinPortal: true }}
          onChange={(date) => onChange(date ? withTime(date, time || dayjs().format("HH:mm")) : null)}
        />
        <Select
          placeholder="--:--"
          data={times}
          value={time}
          searchable
          allowDeselect={false}
          maxDropdownHeight={220}
          comboboxProps={{ withinPortal: true }}
          onChange={(next) => onChange(next ? withTime(current, next) : null)}
        />
      </SimpleGrid>
    </Input.Wrapper>
  );
};

const ACTIONS = [
  { key: "done", label: "Done", icon: IconCheck, color: "green" },
  { key: "reschedule", label: "Reschedule", icon: IconClock, color: "blue" },
  { key: "cancel", label: "Cancel", icon: IconX, color: "red" },
];

const emptyForm = (followup) => ({
  outcome: "INTERESTED",
  notes: "",
  schedule_another: true,
  next_due_at: null,
  next_type: followup?.followup_type || "OUTBOUND_CALL",
  next_priority: followup?.priority || "MEDIUM",
  next_notes: "",
  new_due_at: null,
  reason: "",
});

const FollowupDoneButton = ({ followup, followupId, status, onCompleted }) => {
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const leadId = followup?.lead;
  const { data: timeline } = useSWR(
    (action === "done" || action === "cancel") && leadId ? `crm/leads/${leadId}/timeline/?page_size=20` : null,
    fetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );
  const followupEvents = (timeline?.results || []).filter((event) =>
    String(event.event_type || "").includes("FOLLOWUP")
  );
  const matched = followupEvents.filter((event) => Number(event.metadata?.followup_id) === Number(followupId));
  const history = (matched.length ? matched : followupEvents).slice(0, 3);

  if (!followupId) return null;
  if (status === "DONE" || status === "CANCELLED") {
    const done = status === "DONE";
    return (
      <Badge
        radius="xl"
        variant="filled"
        tt="none"
        styles={{
          root: { background: done ? "#D1FFD9" : "#FFE1D8", color: done ? "#168A32" : "#C92A2A", height: 30 },
          label: { color: done ? "#168A32" : "#C92A2A", fontSize: 12, fontWeight: 600 },
        }}
      >
        {done ? "Completed" : "Cancelled"}
      </Badge>
    );
  }

  const patch = (next) => setForm((prev) => ({ ...prev, ...next }));
  const open = (next) => {
    setForm(emptyForm(followup));
    setAction(next);
  };

  const invalid =
    (action === "done" && (!form.outcome || (form.schedule_another && !form.next_due_at))) ||
    (action === "reschedule" && (!form.new_due_at || !form.reason.trim())) ||
    (action === "cancel" && !form.reason.trim());

  const submit = async () => {
    setBusy(true);
    try {
      if (action === "done") {
        const payload = {
          outcome: form.outcome,
          notes: form.notes,
          schedule_another: form.schedule_another,
        };
        if (form.schedule_another) {
          payload.next_followup = {
            due_at: dayjs(form.next_due_at).format("YYYY-MM-DDTHH:mm"),
            type: form.next_type,
            priority: form.next_priority,
            notes: form.next_notes,
          };
        }
        await fetcher(`crm/followups/${followupId}/complete/`, "POST", payload);
      } else if (action === "reschedule") {
        await fetcher(`crm/followups/${followupId}/reschedule/`, "POST", {
          new_due_at: dayjs(form.new_due_at).format("YYYY-MM-DDTHH:mm"),
          reason: form.reason,
          notes: form.notes,
        });
      } else {
        await fetcher(`crm/followups/${followupId}/cancel/`, "POST", { reason: form.reason });
      }
      successNotifications(SUCCESS[action]);
      setAction(null);
      if (typeof onCompleted === "function") await onCompleted();
    } catch (error) {
      errorNotifications(error?.response?.data || {}, error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Menu position="bottom-end" withinPortal shadow="md">
        <Menu.Target>
          <Button
            size="xs"
            radius="xl"
            variant="outline"
            rightSection={<IconChevronDown size={14} />}
            onClick={(event) => event.stopPropagation()}
          >
            Actions
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {ACTIONS.map(({ key, label, icon: Icon, color }) => (
            <Menu.Item key={key} color={color} leftSection={<Icon size={14} />} onClick={() => open(key)}>
              {label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      <Modal opened={!!action} onClose={() => !busy && setAction(null)} title={TITLE[action]} size={action === "cancel" ? "md" : "lg"} centered>
        <Stack>
          {action === "done" ? (
            <>
              <Select
                label="Outcome"
                withAsterisk
                data={OUTCOMES}
                value={form.outcome}
                onChange={(value) => patch({ outcome: value || "INTERESTED" })}
              />
              <Textarea label="Notes" value={form.notes} onChange={(e) => patch({ notes: e.target.value })} />
              <Checkbox
                label="Schedule another"
                checked={form.schedule_another}
                onChange={(e) => patch({ schedule_another: e.currentTarget.checked })}
              />
              {form.schedule_another ? (
                <>
                  <DueDateTime
                    label="Next due"
                    value={form.next_due_at}
                    onChange={(value) => patch({ next_due_at: value })}
                  />
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    <Select
                      label="Type"
                      data={FOLLOWUP_TYPE_OPTIONS}
                      value={form.next_type}
                      allowDeselect={false}
                      onChange={(value) => patch({ next_type: value || "OUTBOUND_CALL" })}
                    />
                    <Select
                      label="Priority"
                      data={FOLLOWUP_PRIORITY_OPTIONS}
                      value={form.next_priority}
                      allowDeselect={false}
                      onChange={(value) => patch({ next_priority: value || "MEDIUM" })}
                    />
                  </SimpleGrid>
                  <Textarea
                    label="Next notes"
                    value={form.next_notes}
                    onChange={(e) => patch({ next_notes: e.target.value })}
                  />
                </>
              ) : null}
              <RecentHistory items={history} />
            </>
          ) : null}
          {action === "reschedule" ? (
            <>
              <DueDateTime
                label="New due"
                value={form.new_due_at}
                onChange={(value) => patch({ new_due_at: value })}
              />
              <Textarea
                label="Reason"
                withAsterisk
                required
                value={form.reason}
                onChange={(e) => patch({ reason: e.target.value })}
              />
              <Textarea label="Notes" value={form.notes} onChange={(e) => patch({ notes: e.target.value })} />
            </>
          ) : null}
          {action === "cancel" ? (
            <>
              <Text size="sm">
                <Text span fw={700}>{followup?.person_name || "—"}</Text>
                {followup?.followup_type ? ` · ${String(followup.followup_type).replaceAll("_", " ")}` : ""}
                {followup?.due_date ? ` · due ${dayjs(followup.due_date).format("DD MMM YYYY, hh:mm a")}` : ""}
              </Text>
              <Textarea
                label="Reason"
                placeholder="Why this follow-up is cancelled"
                withAsterisk
                required
                minRows={3}
                value={form.reason}
                onChange={(e) => patch({ reason: e.target.value })}
              />
              <RecentHistory items={history} />
            </>
          ) : null}
          <Group justify="flex-start">
            <Button
              color={action === "cancel" ? "red" : action === "done" ? "orange" : "blue"}
              loading={busy}
              disabled={invalid}
              onClick={submit}
            >
              {SAVE[action]}
            </Button>
            <Button variant="default" onClick={() => setAction(null)} disabled={busy}>
              Close
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default FollowupDoneButton;
